import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MessageCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import ContactSection from "../components/home/ContactSection";
import { SERVICES, getServiceBySlug, whatsappLink } from "../data/services";

export default function ServicioDetallePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) {
      document.title = `${service.title} | Ospino & Piñeres Abogados`;
      window.scrollTo(0, 0);
    }
  }, [service]);

  if (!service) return <Navigate to="/servicios" replace />;

  const Icon = service.icon;
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      <section className="pt-36 pb-20 md:pt-44 md:pb-24 bg-white border-b border-slate-100">
        <div className="container-x">
          <Link
            to="/servicios"
            data-testid="service-back"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 transition-colors mb-10"
          >
            <ArrowLeft size={15} />
            Todas las áreas de práctica
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-8">
              <Icon size={36} strokeWidth={1.4} style={{ color: "#C5A059" }} className="mb-6" />
              <div className="flex items-center gap-3 mb-4">
                <span className="gold-rule" />
                <span className="overline">Servicio</span>
              </div>
              <h1 className="font-serif text-navy-900 text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05]">
                {service.title}
              </h1>
              <p className="mt-7 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
                {service.description}
              </p>
            </Reveal>

            <Reveal className="lg:col-span-4" delay={120}>
              <div
                className="p-8 h-full flex flex-col justify-between"
                style={{ background: "#0A192F" }}
              >
                <div>
                  <span
                    className="text-xs font-bold uppercase tracking-[0.22em]"
                    style={{ color: "#C5A059" }}
                  >
                    Solicita una asesoría
                  </span>
                  <p className="mt-4 text-white font-serif text-2xl leading-snug">
                    Cuéntanos tu caso y un asesor especializado te contactará.
                  </p>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <a
                    href={whatsappLink(`Hola, me interesa información sobre ${service.title}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-cta-whatsapp"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm text-sm font-medium text-navy-900 transition-all"
                    style={{ background: "#C5A059" }}
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </a>
                  <Link
                    to="/contacto"
                    data-testid="service-cta-form"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-sm text-sm font-medium text-white border border-white/40 hover:bg-white hover:text-navy-900 transition-all"
                  >
                    Escribir mensaje
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="gold-rule" />
              <span className="overline">Alcance</span>
            </div>
            <h2 className="font-serif text-navy-900 text-3xl md:text-4xl leading-tight">
              Qué incluye este servicio
            </h2>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <ul className="space-y-5">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-4 pb-5 border-b border-slate-100 last:border-0">
                  <span
                    className="mt-1 flex-shrink-0 inline-flex items-center justify-center w-6 h-6"
                    style={{ background: "rgba(197, 160, 89, 0.12)" }}
                  >
                    <Check size={14} strokeWidth={2.5} style={{ color: "#C5A059" }} />
                  </span>
                  <span className="text-slate-700 leading-relaxed text-[16px]">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16" style={{ background: "#F8F9FA" }}>
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <span className="overline">Siguiente área</span>
            <p className="mt-2 font-serif text-2xl text-navy-900">{next.title}</p>
          </div>
          <Link
            to={`/servicios/${next.slug}`}
            data-testid="service-next"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-navy-900 text-white text-sm font-medium rounded-sm hover:bg-navy-800 transition-colors"
          >
            Continuar
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <ContactSection defaultService={service.title} />
    </>
  );
}